// ─────────────────────────────────────────────────────────────────────────────
// Mack primitives — the surfaces and controls shared across Mack's tabs.
// Extracted from MackPage.tsx in build-04. Every style object and component
// here is moved VERBATIM; the extraction is a pure refactor and the rendered
// output is byte-identical before and after.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { C, MONO, SANS } from './tokens';

// ── STYLE HELPERS ────────────────────────────────────────────────────────────
export const card: React.CSSProperties = {
  background: C.card,
  border: `1px solid ${C.cardBorder}`,
  borderRadius: 10,
};

export function PhasesEvidenceSurface({ children }: { children: React.ReactNode }) {
  return <section style={{ ...card, padding: 20,
    boxShadow: '0 12px 28px rgba(0,0,0,0.32), inset 0 1px 0 rgba(237,237,237,0.04)' }}>
    {children}
  </section>;
}

// Amber panel heading — "Calendar", "Activation forecast", "Unscheduled — …".
export const panelTitle: React.CSSProperties = {
  fontSize: 15, fontWeight: 700, color: C.amber, letterSpacing: '0.005em',
};

export const panelNote: React.CSSProperties = {
  fontSize: 13, color: C.textSub, lineHeight: 1.6, marginTop: 9,
};

// Outline pill — mono, uppercase, letter-spaced, transparent fill.
export const outlinePill = (colour: string): React.CSSProperties => ({
  fontSize: 11, fontFamily: MONO, fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.14em',
  padding: '5px 13px', borderRadius: 20,
  border: `1px solid ${colour}`, color: colour, background: 'transparent',
  whiteSpace: 'nowrap',
});

// Tiny mono column/group label — "COUNTRY", "SHEET", "A NOW".
export const microLabel: React.CSSProperties = {
  fontSize: 9.5, fontFamily: MONO, color: C.textMuted,
  textTransform: 'uppercase', letterSpacing: '0.14em',
};

export function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        fontSize: 13, fontFamily: SANS,
        fontWeight: active ? 700 : 400,
        padding: '7px 17px', borderRadius: 8, cursor: 'pointer',
        border: `1px solid ${active ? C.chipBorder : C.chipOffBd}`,
        background: active ? C.chipBg : C.chipOffBg,
        color: active ? C.amberBright : C.textSub,
        transition: 'all 0.15s', whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// Honest empty frame for every tab that build-01 does not land. Not a
// "coming soon" — it names the surface and says what it is waiting on.
export function TabFrame({ title, note }: { title: string; note: string }) {
  return (
    <div style={{ ...card, padding: '22px 22px' }}>
      <div style={panelTitle}>{title}</div>
      <div style={{ ...panelNote, maxWidth: 640 }}>{note}</div>
    </div>
  );
}

// Section caption that sits on the page ground ABOVE its panel, not inside it —
// "MACRO VARIABLES", "INSTRUMENTS PRICED OFF THIS SHEET".
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ ...microLabel, marginBottom: 13 }}>{children}</div>;
}

export function MiniBar({ pct, height = 4 }: { pct: number; height?: number }) {
  return (
    <div style={{ height, borderRadius: height / 2, background: '#3b3b3b', overflow: 'hidden' }}>
      <div style={{
        width: `${Math.max(0, Math.min(1, pct)) * 100}%`, height: '100%',
        background: C.amber, borderRadius: height / 2, transition: 'width 0.25s ease',
      }} />
    </div>
  );
}
